// types
import * as React from 'react'
import { SetStateAction } from 'react'
// libs
import { reset, submit } from 'redux-form'
import { matchPath } from 'react-router'
import { useRouter } from 'hooks/useRouter'
import { useDispatch } from 'react-redux'
import { useSnackbar } from 'notistack'
// hooks
import { useModalData } from 'hooks/index'
// constants
import { Forms, Modals, PLAN_VIEW_MODE } from 'constants/index'
// context
import { SelectedPlanItemsContext } from 'containers/SubPlan/layers/BL/SelectedItems'
import { SubPlanStepperAPIContext } from 'containers/SubPlan/layers/API'
import { IField, IPlan } from 'types/graphql'
// utils
import { isEmpty } from 'lodash'
import { getSnackbarCloseButton } from 'providers/SnackbarProvider/utils'

type Props = React.PropsWithChildren<unknown>

export declare namespace NSubPlanStepperBLContext {
  type THandleCreatePlan = (plan: IPlan) => (data: any, _: any, { fields }: { fields: IField[] }) => Promise<void>
  type THandleUpdatePlan = () => (data: any, _: any, { fields }: { fields: IField[] }) => Promise<void>

  interface IContext {
    activeStep: number
    handlePreviousClick: () => void
    handleCancelClick: (plan: IPlan) => () => void
    handleNextClick: () => void
    handleSubmitClick: () => void
    handleSetAnyTouched: (value: boolean) => SetStateAction<boolean>
    handleCreatePlan: THandleCreatePlan
    handleUpdatePlan: THandleUpdatePlan
    handleFormSubmit: THandleCreatePlan | THandleUpdatePlan
  }
}

export const SubPlanStepperBLContext = React.createContext<NSubPlanStepperBLContext.IContext & any>({} as any)

const SubPlanStepperBL: React.FC<Props> = ({ children }) => {
  const [activeStep, setActiveStep] = React.useState<number>(0)
  const [anyTouched, setAnyTouched] = React.useState<boolean>(false)

  const { createdPlan, createPlan, updatePlan, parentPlan } = React.useContext(SubPlanStepperAPIContext)
  const { selectedPlanItems } = React.useContext(SelectedPlanItemsContext)

  const { hideModal } = useModalData({ modalType: Modals.SUB_PLAN_CREATE_MODAL })

  const { enqueueSnackbar, closeSnackbar } = useSnackbar()

  const router = useRouter()
  const dispatch = useDispatch()

  // methods
  const handleSetAnyTouched = (value: boolean) => setAnyTouched(value)

  const handleNext = () => setActiveStep((prevActiveStep) => prevActiveStep + 1)
  const handleBack = () => setActiveStep((prevActiveStep) => prevActiveStep - 1)

  const submitForm = () => dispatch(submit(Forms.CREATE_SUB_PLAN_FORM))
  const resetForm = () => dispatch(reset(Forms.CREATE_SUB_PLAN_FORM))

  const handlePreviousClick = () => handleBack()

  const handleNextClick = () => {
    if (activeStep === 0) {
      submitForm()
    }

    if (activeStep === 1) {
      if (isEmpty(selectedPlanItems)) {
        enqueueSnackbar('Please select at least 1 item from the parent plan below!', {
          variant: 'warning',
          action: (key) => getSnackbarCloseButton(key, closeSnackbar)
        })
        return
      }
      handleNext()
    }

    if (activeStep === 2) {
      handleSubmitClick()
    }
  }

  const handleCancelClick = (plan: IPlan) => async () => {
    const slug = router.query.slug?.[0]
    const isPlanPage = !!matchPath('/plans/:companyId/edit/:id', router.pathname)

    const href = {
      pathname: isPlanPage ? `/plans/${plan.companyId}/edit/${plan.id}` : slug ? `/plans/${slug}` : 'plans',
      query: {
        ...(router.query.id && {
          planViewMode: router.query.planViewMode
        })
      }
    }

    await router.replace(href, undefined, { shallow: true })

    resetForm()
    hideModal()
  }

  const handleSubmitClick = async () => {
    await router.push({
      pathname: `/plans/${createdPlan.companyId}/edit/${createdPlan.id}`,
      query: {
        parentId: parentPlan.id,
        planViewMode: PLAN_VIEW_MODE.STRUCTURED
      }
    })

    resetForm()
    hideModal()
  }

  const handleCreatePlan =
    (plan: IPlan) =>
    async (data, _, { fields }) => {
      await createPlan(data, fields, plan)
      handleSetAnyTouched(false)
      handleNext()
    }

  const handleUpdatePlan =
    () =>
    async (data, _, { fields }) => {
      if (!anyTouched) {
        return handleNext()
      }

      await updatePlan(data, fields)
      handleSetAnyTouched(false)
      handleNext()
    }

  const handleFormSubmit = createdPlan ? handleUpdatePlan : handleCreatePlan

  const value = {
    activeStep,
    handleCancelClick,
    handleNextClick,
    handlePreviousClick,
    handleSubmitClick,
    handleSetAnyTouched,
    handleCreatePlan,
    handleUpdatePlan,
    handleFormSubmit
  }

  return <SubPlanStepperBLContext.Provider value={value}>{children}</SubPlanStepperBLContext.Provider>
}

export default SubPlanStepperBL
